import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";

// Lazy load all pages for code splitting
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const FreePreviewForm = lazy(() => import("@/pages/FreePreviewForm"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const ProcessPage = lazy(() => import("@/pages/ProcessPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const EmploymentPage = lazy(() => import("@/pages/EmploymentPage"));
const EmploymentApplyPage = lazy(() => import("@/pages/EmploymentApplyPage"));

// Minimal loading fallback that matches the dark theme
const PageLoader = () => (
  <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function FloatingFreePreviewButton() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(pathname !== "/");

  useEffect(() => {
    // On non-home pages: show immediately.
    if (pathname !== "/") {
      setVisible(true);
      return;
    }
    // On home: hidden until the hero "Get Free Preview" CTA scrolls out of view.
    setVisible(false);

    let observer = null;
    let scrollHandler = null;
    let attachedToMarker = false;

    const attach = () => {
      const marker = document.getElementById("hero-free-preview-cta");
      if (marker) {
        attachedToMarker = true;
        observer = new IntersectionObserver(
          ([entry]) => {
            // Show the floating pill only once the hero CTA has been fully scrolled past.
            setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 }
        );
        observer.observe(marker);
        return true;
      }
      return false;
    };

    if (!attach()) {
      // Poll for the marker (LandingPage is lazy-loaded and may mount after this effect).
      let tries = 0;
      const interval = setInterval(() => {
        if (attach() || ++tries > 40) clearInterval(interval);
      }, 150);
      // Cleanup + scroll fallback that respects the hero height (avoid popping up too early).
      scrollHandler = () => {
        if (attachedToMarker) return;
        // Only reveal once we've scrolled well past the hero (approx one full viewport).
        setVisible(window.scrollY > window.innerHeight * 1.1);
      };
      window.addEventListener("scroll", scrollHandler, { passive: true });
      return () => {
        clearInterval(interval);
        if (observer) observer.disconnect();
        if (scrollHandler) window.removeEventListener("scroll", scrollHandler);
      };
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [pathname]);

  if (pathname.startsWith("/admin")) return null;
  if (pathname === "/freepreview") return null;
  // Hide on the Apply forms — the CTA competes with the primary "Send application" button
  if (pathname.startsWith("/jobs/apply") || pathname.startsWith("/employment/apply")) return null;
  // Hide on the Careers page + all its aliases — the role cards each have their own primary
  // "Apply for this role" CTAs, and the floating pill was overlapping them on mobile.
  if (["/jobs", "/employment", "/careers", "/career", "/job"].includes(pathname)) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25 }}
          className="md:hidden fixed bottom-5 inset-x-0 z-50 flex justify-center pointer-events-none px-4"
        >
          <a
            href="/freepreview"
            className="pointer-events-auto bg-cyan-400 text-[#0a1628] px-6 py-3 rounded-full font-body font-bold text-sm shadow-xl shadow-cyan-400/40 flex items-center gap-2 hover:bg-cyan-300 active:scale-95 transition-colors"
            data-testid="floating-free-preview-btn"
            aria-label="Get free preview"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#0a1628" stroke="none"><path d="M8 5v14l11-7z"/></svg>
            Free Preview
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsProvider>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/freepreview" element={<FreePreviewForm />} />
              <Route path="/free-preview" element={<Navigate to="/freepreview" replace />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/employment" element={<EmploymentPage />} />
              <Route path="/jobs" element={<EmploymentPage />} />
              <Route path="/employment/apply/:role" element={<EmploymentApplyPage />} />
              <Route path="/jobs/apply/:role" element={<EmploymentApplyPage />} />
              <Route path="/careers" element={<Navigate to="/jobs" replace />} />
              <Route path="/career" element={<Navigate to="/jobs" replace />} />
              <Route path="/job" element={<Navigate to="/jobs" replace />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/policy" element={<PolicyPage />} />
              <Route path="/terms" element={<PolicyPage />} />
              <Route path="/privacy" element={<PolicyPage />} />
            </Routes>
          </Suspense>
          {/* Floating Free Preview button - mobile only, hidden on admin & the preview form itself */}
          <FloatingFreePreviewButton />
        </AnalyticsProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
